// Expenses screen — list, filter, search, edit, delete (spec §6).

import { iconPlain } from '../lib/icons';
import { state } from '../state';
import { renderAddButton, renderNav, renderOfflineBanner, expenseRow, emptyState, escapeHtml } from './shared';

function filteredList() {
  const f = state.filters;
  let list = [...state.expenses].sort((a, b) => b.date.localeCompare(a.date));
  if (f.category !== 'all') list = list.filter((e) => e.categoryId === f.category);
  if (f.currency !== 'all') list = list.filter((e) => e.currency === f.currency);
  if (f.search) list = list.filter((e) => (e.explanation || '').toLowerCase().includes(f.search.toLowerCase()));
  return list;
}

export function renderExpenses(): string {
  const f = state.filters;
  const list = filteredList();

  return `
  <div class="screen">
    ${renderOfflineBanner()}
    <div class="topbar"><div><h1>Expenses</h1><div class="sub">${list.length} of ${state.expenses.length} shown</div></div>
      ${renderAddButton()}
    </div>
    <div class="search-wrap">
      <label class="visually-hidden" for="fsearch">Search explanation</label>
      <input class="fsearch" id="fsearch" placeholder="Search explanation…" value="${escapeHtml(f.search)}" oninput="App.setSearchFilter(this.value)"/>
    </div>
    <div class="filter-bar" role="group" aria-label="Filter by category">
      <button class="filter-chip ${f.category === 'all' ? 'active' : ''}" onclick="App.setCategoryFilter('all')">All categories</button>
      ${state.categories
        .map(
          (c) =>
            `<button class="filter-chip ${f.category === c.id ? 'active' : ''}" onclick="App.setCategoryFilter('${c.id}')">${iconPlain(
              c.icon
            )} ${escapeHtml(c.name)}</button>`
        )
        .join('')}
    </div>
    <div class="filter-bar" style="padding-top:8px;" role="group" aria-label="Filter by currency">
      <button class="filter-chip ${f.currency === 'all' ? 'active' : ''}" onclick="App.setCurrencyFilter('all')">All currencies</button>
      ${state.currencies
        .map((c) => `<button class="filter-chip ${f.currency === c.code ? 'active' : ''}" onclick="App.setCurrencyFilter('${c.code}')">${c.code}</button>`)
        .join('')}
    </div>
    <div id="expenseListWrap" style="margin-top:8px;">
      ${renderList(list)}
    </div>
  </div>
  ${renderNav()}`;
}

function renderList(list: ReturnType<typeof filteredList>): string {
  return list.length ? list.map(expenseRow).join('') : emptyState('search', 'No matching expenses', 'Try adjusting your filters or search.');
}

/** Re-renders just the list in place (used for the search box's oninput so we don't
 * rebuild filter chips / lose input focus on every keystroke). */
export function refreshExpenseList(): void {
  const wrap = document.getElementById('expenseListWrap');
  if (wrap) wrap.innerHTML = renderList(filteredList());
}

export function renderExpenseDetailSheet(id: string): string {
  const e = state.expenses.find((x) => x.id === id);
  if (!e) return '';
  return `
    <div class="sheet-head"><h2>Edit expense</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
    <div class="sheet-body">
      <form onsubmit="return App.saveExpenseEdit(event,'${id}')">
        <div class="field amount"><label for="edAmount">Amount</label><input type="number" step="0.01" min="0.01" id="edAmount" value="${e.amount}" required/></div>
        <div class="row2">
          <div class="field"><label for="edDate">Date</label><input type="date" id="edDate" value="${e.date}" required/></div>
          <div class="field"><label for="edCurrency">Currency</label><select id="edCurrency">${state.currencies
            .map((c) => `<option value="${c.code}" ${c.code === e.currency ? 'selected' : ''}>${c.code}</option>`)
            .join('')}</select></div>
        </div>
        <div class="field"><label for="edCategory">Category</label><select id="edCategory">${state.categories
          .map((c) => `<option value="${c.id}" ${c.id === e.categoryId ? 'selected' : ''}>${escapeHtml(c.name)}</option>`)
          .join('')}</select></div>
        <div class="field"><label for="edNote">Explanation</label><input type="text" id="edNote" value="${escapeHtml(e.explanation || '')}"/></div>
        <button type="submit" class="btn btn-primary btn-block">Save changes</button>
        <div style="height:10px;"></div>
        <button type="button" class="btn btn-danger" style="width:100%;" onclick="App.confirmDeleteExpense('${id}')">Delete expense</button>
      </form>
    </div>`;
}

export function renderDeleteExpenseSheet(id: string): string {
  return `
    <div class="sheet-head"><h2>Delete expense?</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
    <div class="sheet-body">
      <p style="color:var(--text-dim);font-size:13.5px;">This can't be undone.</p>
      <button class="btn btn-danger" style="width:100%;background:rgba(240,85,76,.12);" onclick="App.doDeleteExpense('${id}')">Delete permanently</button>
      <div style="height:10px;"></div>
      <button class="btn btn-ghost" style="width:100%;" onclick="App.closeSheet()">Cancel</button>
    </div>`;
}

export function renderAddExpenseSheet(): string {
  return `
    <div class="sheet-head"><h2>Add expense</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
    <div class="sheet-body">
      <form onsubmit="return App.saveNewExpense(event)">
        <div class="field amount"><label for="naAmount">Amount</label><input type="number" step="0.01" min="0.01" id="naAmount" placeholder="0.00" required/></div>
        <div class="row2">
          <div class="field"><label for="naDate">Date</label><input type="date" id="naDate" value="${todayValue()}" required/></div>
          <div class="field"><label for="naCurrency">Currency</label><select id="naCurrency">${state.currencies
            .map((c) => `<option value="${c.code}" ${c.code === state.lastCurrency ? 'selected' : ''}>${c.code}</option>`)
            .join('')}</select></div>
        </div>
        <div class="field"><label for="naCategory">Category</label><select id="naCategory">${state.categories
          .map((c) => `<option value="${c.id}">${escapeHtml(c.name)}</option>`)
          .join('')}</select></div>
        <div class="field"><label for="naNote">Explanation</label><input type="text" id="naNote" placeholder="What was this expense for?"/></div>
        <button type="submit" class="btn btn-primary btn-block">＋ Add expense</button>
      </form>
    </div>`;
}

function todayValue(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
